import React, { useEffect, useMemo, useRef } from "react";
import slide1 from "./../assets/slide_1.mp4";
import slide2 from "./../assets/slide_2.mp4";
import slide3 from "./../assets/slide_3.mp4";
import slide4 from "./../assets/slide_4.mp4";
import { ReactComponent as InstantOutfit } from "./../assets/svgs/instant-outfit.svg";
import { ReactComponent as PersonalizedSizing } from "./../assets/svgs/personalized-sizing.svg";
import { ReactComponent as RealTimeAnalysis } from "./../assets/svgs/real-time-anaysis.svg";
import { Link } from "react-router-dom";

const ProductShowcaseRotator = () => {
  const items = useMemo(
    () => [
      {
        tab: "PERSONALIZED AI",
        title: "Personal Fashion Stylist\nPowered by AI",
        bullets: [
          "Personalized sizing and fit predictions.",
          "Real-time style analysis using computer vision.",
          "Instant outfit recommendations based on user preferences.",
        ],
        icons: [PersonalizedSizing, RealTimeAnalysis, InstantOutfit],
        button: { label: "Get started" },
        videoSrc: slide1,
      },
      {
        tab: "ALGORITHM",
        title: "Personalized Recommendation\nAlgorithm",
        bullets: [
          "Filters your product page with style and fit science",
          "Based on real user features",
          "Highlights products with the smallest chance of return",
        ],
        icons: [PersonalizedSizing, RealTimeAnalysis, InstantOutfit],
        button: { label: "Get started" },
        videoSrc: slide2,
      },
      {
        tab: "SMS AND EMAIL SALES AGENT",
        title: "Personalized SMS &\nEmail Marketing agent",
        bullets: [
          "Interactive user experience for SMS Marketing",
          "Anonymized segment led marketing (by sizing, body and face shape, color analysis, height, and more)",
          "Personalized with Agentic Capabilities",
        ],
        icons: [PersonalizedSizing, RealTimeAnalysis, InstantOutfit],
        button: { label: "Get started" },
        videoSrc: slide3,
      },
      {
        tab: "PRODUCT PAGE",
        title: "Product Details Page\nPowered by AI",
        bullets: [
          "Fit technology to reduce returns",
          "Virtual Try-On Capabilities",
          "Stylist Assistance",
        ],
        icons: [PersonalizedSizing, RealTimeAnalysis, InstantOutfit],
        button: { label: "Get started" },
        videoSrc: slide4,
      },
    ],
    [],
  );

  const videoRefs = useRef([]);
  const hasPlayedRef = useRef([]);
  const endedHandlersRef = useRef([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const video = entry.target;
          const index = Number(video.dataset.index);

          if (entry.isIntersecting) {
            if (!hasPlayedRef.current[index] && video.paused && !video.ended) {
              const playPromise = video.play();
              if (playPromise && typeof playPromise.catch === "function") {
                playPromise.catch(() => {});
              }
            }
          } else {
            if (!video.paused && !video.ended) {
              video.pause();
            }
          }
        });
      },
      { threshold: 0.5 },
    );

    const videos = videoRefs.current.filter(Boolean);

    videos.forEach((video, index) => {
      hasPlayedRef.current[index] = false;

      const handleEnded = () => {
        hasPlayedRef.current[index] = true;
      };

      endedHandlersRef.current[index] = handleEnded;
      video.addEventListener("ended", handleEnded);
      observer.observe(video);
    });

    return () => {
      videos.forEach((video, index) => {
        observer.unobserve(video);
        const handler = endedHandlersRef.current[index];
        if (handler) {
          video.removeEventListener("ended", handler);
        }
      });
      observer.disconnect();
    };
  }, [items]);

  return (
    <section className="pb-[60px]">
      <div className="pb-4 md:pb-6 bg-[#E9E9E9]">
        {items.map((item, idx) => (
          <div
            key={idx}
            className="bg-[#E9E9E9] rounded-[10px] px-4 md:px-6 pt-4 md:pt-6"
          >
            <div className="bg-white rounded-[8px] overflow-hidden border border-[#E6E6E6]">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
                <div className="order-1 lg:order-2 rounded-[10px] overflow-hidden my-[10px] md:my-[30px] md:mr-[27px] bg-[#17171C] px-0 md:px-[33px] h-[400px] md:h-[530px] mx-[5px] md:mx-0">
                  <video
                    ref={(el) => {
                      videoRefs.current[idx] = el;
                    }}
                    data-index={idx}
                    className="w-full h-[380px] md:h-[630px] object-cover pl-[10px] md:pl-0 mt-[20px] md:mt-0"
                    src={item.videoSrc}
                    muted
                    playsInline
                    preload="metadata"
                  />
                </div>

                <div className="order-2 lg:order-1 px-6 md:px-10 py-[10px]">
                  <h2 className="text-black whitespace-pre-line font-normal text-[32px] md:text-[36px] leading-[56px] tracking-[-1.12px] align-middle">
                    {item.title}
                  </h2>

                  <div className="flex flex-col justify-between md:h-[437px]">
                    <ul className="mt-6 space-y-3">
                      {item.bullets.map((bullet, i) => {
                        const Icon = item.icons?.[i];
                        return (
                          <li
                            key={i}
                            className="flex items-start gap-[6px] text-[#4A5565] font-normal text-[14px] leading-[23.4px] tracking-[0.1px]"
                          >
                            {Icon ? (
                              <span className="mt-[3px] shrink-0">
                                <Icon
                                  className="w-[18px] h-[18px]"
                                  aria-hidden="true"
                                />
                              </span>
                            ) : null}
                            <span>{bullet}</span>
                          </li>
                        );
                      })}
                    </ul>

                    <div className="mt-10 flex gap-3">
                      <Link to="/get-started">
                        <button
                          key={item.button.label}
                          type="button"
                          className="w-[120px] h-[48px] rounded-[8px] bg-gradient-to-r from-[#E2D8D3] via-[#D7A09B] to-[#D1C6C0] text-white text-[14px] leading-none font-normal flex items-center justify-center shadow-[0_18px_45px_rgba(0,0,0,0.10)] transition-transform duration-200 hover:scale-[1.01] active:scale-[0.99]"
                        >
                          {item.button.label}
                        </button>
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

export default ProductShowcaseRotator;